import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getPolls, createPoll, votePoll, removeVote, updatePoll, deletePoll } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { format, parseISO, isPast } from 'date-fns';
import {
  PlusIcon, CheckCircleIcon, LockClosedIcon, TrashIcon,
  ChartBarIcon, UserGroupIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

function PollCard({ poll, onVote, onRemoveVote, onClose, onDelete, isAdmin }) {
  const totalVotes = poll.total_votes;
  const userVotedOptionId = poll.options.find((o) => o.user_voted)?.id;
  const isClosed = poll.status === 'closed';

  return (
    <div className="card space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold text-white">{poll.title}</h3>
            {isClosed ? (
              <span className="badge bg-gray-800 text-gray-400 flex items-center gap-1">
                <LockClosedIcon className="w-3 h-3" /> Closed
              </span>
            ) : (
              <span className="badge bg-green-900/50 text-green-300">Open</span>
            )}
          </div>
          {poll.description && <p className="text-sm text-gray-400 mt-1">{poll.description}</p>}
          <div className="text-xs text-gray-600 mt-1">
            By {poll.created_by?.full_name} · {format(parseISO(poll.created_at), 'dd MMM yyyy')}
            {poll.closes_at && !isClosed && (
              <> · Closes {format(parseISO(poll.closes_at), 'dd MMM yyyy')}</>
            )}
          </div>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2 flex-shrink-0">
            {!isClosed && (
              <button
                onClick={() => onClose(poll.id)}
                className="text-xs text-amber-400 hover:text-amber-300 px-2 py-1 rounded border border-amber-800/50 hover:border-amber-700"
              >
                Close poll
              </button>
            )}
            <button
              onClick={() => onDelete(poll.id)}
              className="p-1.5 text-gray-600 hover:text-red-400 rounded transition-colors"
            >
              <TrashIcon className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* Options */}
      <div className="space-y-2">
        {poll.options.map((option) => {
          const pct = totalVotes > 0 ? Math.round((option.vote_count / totalVotes) * 100) : 0;
          const isMyVote = option.user_voted;
          return (
            <div key={option.id} className="space-y-1">
              <div className="flex items-center justify-between gap-2">
                <button
                  disabled={isClosed}
                  onClick={() => isMyVote ? onRemoveVote(poll.id) : onVote(poll.id, option.id)}
                  className={`flex-1 text-left px-3 py-2 rounded-lg border text-sm transition-all ${
                    isMyVote
                      ? 'border-primary-500 bg-primary-600/20 text-primary-300'
                      : isClosed
                      ? 'border-gray-800 bg-surface-100 text-gray-400 cursor-default'
                      : 'border-gray-700 bg-surface-100 text-gray-300 hover:border-primary-600 hover:text-white'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    {isMyVote && <CheckCircleIcon className="w-4 h-4 text-primary-400 flex-shrink-0" />}
                    <span>{option.label}</span>
                  </div>
                </button>
                <div className="text-right flex-shrink-0 w-16">
                  <div className="text-sm font-semibold text-white">{pct}%</div>
                  <div className="text-xs text-gray-600">{option.vote_count} vote{option.vote_count !== 1 ? 's' : ''}</div>
                </div>
              </div>
              {/* Progress bar */}
              <div className="h-1.5 rounded-full bg-surface-200 overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${isMyVote ? 'bg-primary-500' : 'bg-gray-600'}`}
                  style={{ width: `${pct}%` }}
                />
              </div>
              {/* Voters */}
              {option.voters.length > 0 && (
                <div className="flex items-center gap-1 pl-1">
                  <UserGroupIcon className="w-3 h-3 text-gray-600" />
                  <span className="text-xs text-gray-600">{option.voters.join(', ')}</span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="text-xs text-gray-600 pt-1 border-t border-gray-800">
        {totalVotes} total vote{totalVotes !== 1 ? 's' : ''}
        {userVotedOptionId && !isClosed && (
          <span className="ml-2 text-gray-500">(click your selection to remove vote)</span>
        )}
      </div>
    </div>
  );
}

function CreatePollModal({ onClose, onCreate }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [options, setOptions] = useState(['', '']);
  const [closesAt, setClosesAt] = useState('');

  const addOption = () => setOptions([...options, '']);
  const updateOption = (i, val) => setOptions(options.map((o, idx) => (idx === i ? val : o)));
  const removeOption = (i) => options.length > 2 && setOptions(options.filter((_, idx) => idx !== i));

  const handleSubmit = (e) => {
    e.preventDefault();
    const filled = options.filter((o) => o.trim());
    if (filled.length < 2) return toast.error('Add at least 2 options');
    onCreate({ title, description, options: filled, closes_at: closesAt || undefined });
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-surface-50 border border-gray-700 rounded-2xl w-full max-w-lg shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800">
          <h2 className="font-semibold text-white">Create Poll</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-white text-xl leading-none">&times;</button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-xs text-gray-400 mb-1">Question / Title *</label>
            <input
              className="input w-full"
              placeholder="e.g. When should we hold the July meeting?"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Description (optional)</label>
            <textarea
              className="input w-full resize-none"
              rows={2}
              placeholder="Any extra context for fellows..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-2">Options *</label>
            <div className="space-y-2">
              {options.map((opt, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input
                    className="input flex-1"
                    placeholder={`Option ${i + 1} — e.g. Tuesday 15 July at 14:00`}
                    value={opt}
                    onChange={(e) => updateOption(i, e.target.value)}
                  />
                  {options.length > 2 && (
                    <button type="button" onClick={() => removeOption(i)} className="text-gray-600 hover:text-red-400">
                      &times;
                    </button>
                  )}
                </div>
              ))}
            </div>
            <button type="button" onClick={addOption} className="mt-2 text-xs text-primary-400 hover:text-primary-300">
              + Add option
            </button>
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Closes on (optional)</label>
            <input
              type="datetime-local"
              className="input w-full"
              value={closesAt}
              onChange={(e) => setClosesAt(e.target.value)}
            />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary flex-1">Cancel</button>
            <button type="submit" className="btn-primary flex-1">Create Poll</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function PollsPage() {
  const { isAdmin } = useAuth();
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);

  const { data, isLoading } = useQuery({ queryKey: ['polls'], queryFn: getPolls });
  const polls = data?.data || [];

  const invalidate = () => qc.invalidateQueries({ queryKey: ['polls'] });

  const mutCreate = useMutation({
    mutationFn: createPoll,
    onSuccess: () => { toast.success('Poll created'); setShowCreate(false); invalidate(); },
    onError: () => toast.error('Failed to create poll'),
  });

  const mutVote = useMutation({
    mutationFn: ({ id, option_id }) => votePoll(id, option_id),
    onSuccess: () => { toast.success('Vote recorded'); invalidate(); },
    onError: () => toast.error('Failed to vote'),
  });

  const mutRemoveVote = useMutation({
    mutationFn: (id) => removeVote(id),
    onSuccess: () => { toast.success('Vote removed'); invalidate(); },
    onError: () => toast.error('Failed to remove vote'),
  });

  const mutClose = useMutation({
    mutationFn: (id) => updatePoll(id, { status: 'closed' }),
    onSuccess: () => { toast.success('Poll closed'); invalidate(); },
    onError: () => toast.error('Failed to close poll'),
  });

  const mutDelete = useMutation({
    mutationFn: deletePoll,
    onSuccess: () => { toast.success('Poll deleted'); invalidate(); },
    onError: () => toast.error('Failed to delete poll'),
  });

  const openPolls   = polls.filter((p) => p.status === 'open');
  const closedPolls = polls.filter((p) => p.status === 'closed');

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-white">Polls</h1>
          <p className="text-gray-500 text-sm mt-0.5">Vote on meeting times, locations, and programme decisions</p>
        </div>
        {isAdmin && (
          <button onClick={() => setShowCreate(true)} className="btn-primary flex items-center gap-2">
            <PlusIcon className="w-4 h-4" /> New Poll
          </button>
        )}
      </div>

      {isLoading ? (
        <p className="text-gray-500 text-sm">Loading polls...</p>
      ) : polls.length === 0 ? (
        <div className="card text-center py-12">
          <ChartBarIcon className="w-10 h-10 text-gray-700 mx-auto mb-3" />
          <p className="text-gray-400 font-medium">No polls yet</p>
          {isAdmin && <p className="text-gray-600 text-sm mt-1">Create a poll to let fellows vote on meeting times or other decisions.</p>}
        </div>
      ) : (
        <div className="space-y-6">
          {openPolls.length > 0 && (
            <div>
              <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">Open Polls</h2>
              <div className="space-y-4">
                {openPolls.map((poll) => (
                  <PollCard
                    key={poll.id}
                    poll={poll}
                    isAdmin={isAdmin}
                    onVote={(id, option_id) => mutVote.mutate({ id, option_id })}
                    onRemoveVote={(id) => mutRemoveVote.mutate(id)}
                    onClose={(id) => mutClose.mutate(id)}
                    onDelete={(id) => { if (window.confirm('Delete this poll?')) mutDelete.mutate(id); }}
                  />
                ))}
              </div>
            </div>
          )}
          {closedPolls.length > 0 && (
            <div>
              <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">Closed Polls</h2>
              <div className="space-y-4">
                {closedPolls.map((poll) => (
                  <PollCard
                    key={poll.id}
                    poll={poll}
                    isAdmin={isAdmin}
                    onVote={() => {}}
                    onRemoveVote={() => {}}
                    onClose={() => {}}
                    onDelete={(id) => { if (window.confirm('Delete this poll?')) mutDelete.mutate(id); }}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {showCreate && (
        <CreatePollModal
          onClose={() => setShowCreate(false)}
          onCreate={(data) => mutCreate.mutate(data)}
        />
      )}
    </div>
  );
}
